import React, { FC, useState } from "react"
import { Col, Descriptions, Divider, Tooltip, Typography } from "antd"
import { AppMetierTextInput } from "../../../components/AppMetierTextInput/AppMetierTextInput"
import "./StructureForm.scss"
import {
  AddressUpdateRequest,
  CodeLibelle,
  RegisStructureAddress
} from "../../../client/backend-client/generated"
import { AppMetierTitle } from "../../../components/AppMetierTitle/AppMetierTitle"
import { AddressAutoComplete } from "../../../components/AddressAutoComplete/AddressAutoComplete"
import { AppMetierSwitch } from "../../../components/AppMetierSwitch"
import { CityAutoComplete } from "../../../components/CityAutoComplete/CityAutoComplete"
import { ReferenceCode, useGetReferenceTyped } from "../../../queries/ReferenceQueries"
import { InfoCircleOutlined } from "@ant-design/icons"
import { ReferenceSelect } from "../../../components/AppMetierSelect/ReferenceSelect"

const { Text } = Typography

export const AddressComponent: FC<{
  setData: (data: AddressUpdateRequest | AddressUpdateRequest) => void
  addressData: AddressUpdateRequest | AddressUpdateRequest | undefined
  labelStyle: { width?: string; minWidth?: string }
  showCodeCommune: boolean //false pour porteur
  initialAddress?: RegisStructureAddress // Address data from Regis, untouched
}> = ({ addressData, labelStyle, setData, showCodeCommune, initialAddress }) => {
  const [isManual, setIsManual] = useState(addressData?.code === undefined)
  const { isLoading: isEpciReferenceLoading, data: references } = useGetReferenceTyped<CodeLibelle>(
    ReferenceCode.ZONAGE_EPCI
  )

  const renderAddressField = (
    fieldName: keyof AddressUpdateRequest,
    label: string,
    fixedState?: boolean,
    required: boolean = false
  ) => {
    return (
      <Descriptions.Item
        labelStyle={labelStyle}
        label={
          <Text strong>
            {label}
            {required ? "*" : ""}
          </Text>
        }
      >
        {(fixedState != undefined ? fixedState : isManual) ? (
          <AppMetierTextInput
            maxLength={255}
            inputValue={addressData?.[fieldName]}
            onChange={value => {
              setData({
                ...addressData,
                code: isManual ? undefined : addressData?.code,
                [fieldName]: value !== "" ? value : undefined
              })
            }}
            name=""
            status={required && !addressData?.[fieldName] ? "error" : undefined}
          />
        ) : (
          addressData?.[fieldName]
        )}
      </Descriptions.Item>
    )
  }

  return (
    <div>
      <AppMetierTitle level={4}>Localisation</AppMetierTitle>
      <Descriptions bordered size="middle" column={1}>
        <Descriptions.Item
          labelStyle={labelStyle}
          label={<Text strong>Rechercher une adresse</Text>}
        >
          <AddressAutoComplete
            onChange={value => {
              setData(value)
            }}
            disabled={isManual}
          />
          <Col span={24}>
            <AppMetierSwitch
              checked={isManual}
              label="Saisir une adresse manuellement"
              onChange={() => setIsManual(!isManual)}
              checkedInnerLabel="Oui"
              uncheckedInnerLabel="Non"
            />
          </Col>
        </Descriptions.Item>
        {renderAddressField("adresse1", "Adresse", undefined, true)}
        {renderAddressField("adresse2", "Complément d'adresse", true)}
        {renderAddressField("codePostal", "Code postal", undefined, true)}
        <Descriptions.Item labelStyle={labelStyle} label={<Text strong>Ville*</Text>}>
          {isManual ? (
            <CityAutoComplete
              defaultValue={addressData?.ville}
              onChange={value => {
                setData({
                  ...addressData,
                  ville: value?.ville,
                  codeCommune: value?.codeCommune,
                  code: value?.code
                })
              }}
            />
          ) : (
            addressData?.ville
          )}
        </Descriptions.Item>
        {showCodeCommune ? renderAddressField("codeCommune", "Code commune", false) : null}
        {initialAddress?.epciCode ? (
          <Descriptions.Item
            labelStyle={labelStyle}
            label={
              <div>
                <Text strong>EPCI</Text>
                <Tooltip
                  placement="bottom"
                  title="Établissement public de coopération intercommunale"
                >
                  <InfoCircleOutlined style={{ fontSize: "1em", paddingLeft: "5px" }} />
                </Tooltip>
              </div>
            }
          >
            {isEpciReferenceLoading
              ? ""
              : references?.find(epci => epci.code === initialAddress.epciCode)?.libelle}
          </Descriptions.Item>
        ) : null}
      </Descriptions>
      <Divider />
      <AppMetierTitle level={4}>Zonage territorial</AppMetierTitle>
      <Descriptions bordered size="middle" column={1}>
        <Descriptions.Item
          labelStyle={labelStyle}
          label={<Text strong>France ruralités revitalisation</Text>}
        >
          {initialAddress?.zrr ? "Oui" : "Non"}
        </Descriptions.Item>
        <Descriptions.Item
          labelStyle={labelStyle}
          label={<Text strong>Quartier prioritaire de la politique de la ville</Text>}
        >
          <ReferenceSelect
            referenceCode={ReferenceCode.ZONAGE_QPV}
            allowClear
            disabled={true}
            hideSelectionArrow={true}
            code={initialAddress?.qpvCode?.code}
          />
        </Descriptions.Item>
        <Descriptions.Item labelStyle={labelStyle} label={<Text strong>Action cœur de ville</Text>}>
          {initialAddress?.acvCode ? "Oui" : "Non"}
        </Descriptions.Item>
        <Descriptions.Item
          labelStyle={labelStyle}
          label={<Text strong>Petite ville de demain</Text>}
        >
          {initialAddress?.pvdCode ? "Oui" : "Non"}
        </Descriptions.Item>
      </Descriptions>
    </div>
  )
}
